import React, { useEffect, useRef, useState } from 'react'
import Tray from 'components/shared/Tray'
import styles from 'components/quests/confirm.module.scss'

export default function ConfirmTray({ trayParams, closeTray }) {
    const confirmVariant = trayParams?.confirmVariant || 'bsv'
    const primaryActionFirst = !!trayParams?.primaryActionFirst
    const hideConfirm = !!trayParams?.hideConfirm
    const reasonInput = trayParams?.reasonInput
    const [reasonValue, setReasonValue] = useState(reasonInput?.defaultValue || '')
    const [isConfirming, setIsConfirming] = useState(false)

    const didActRef = useRef(false)
    const isConfirmingRef = useRef(false)
    const openedAtRef = useRef(Date.now())

    const onConfirm = async () => {
        if (trayParams?.confirmDisabled || isConfirmingRef.current) {
            return
        }
        // A tray opened by untrusted content can ask for a moment before Confirm counts, so a
        // click already on its way doesn't land on it.
        if (
            trayParams?.confirmDelayMs &&
            Date.now() - openedAtRef.current < trayParams.confirmDelayMs
        ) {
            return
        }

        if (trayParams?.awaitConfirm) {
            isConfirmingRef.current = true
            setIsConfirming(true)
            try {
                const shouldClose = await trayParams.onConfirm?.(reasonValue.trim())
                if (shouldClose === false) return

                didActRef.current = true
                closeTray()
            } finally {
                isConfirmingRef.current = false
                setIsConfirming(false)
            }
            return
        }

        didActRef.current = true
        closeTray()
        if (trayParams.onConfirm) trayParams.onConfirm(reasonValue.trim())
    }

    const onCancel = () => {
        if (isConfirmingRef.current) return
        didActRef.current = true
        closeTray()
        if (trayParams.onCancel) trayParams.onCancel()
    }

    const onExtraAction = extraAction => {
        if (extraAction?.disabled) {
            return
        }
        didActRef.current = true
        closeTray()
        if (extraAction?.onClick) {
            extraAction.onClick()
        }
    }

    const onSetupLink = link => {
        if (link?.disabled) {
            return
        }
        if (!link?.keepOpen) {
            didActRef.current = true
            closeTray()
        }
        if (link?.onClick) {
            link.onClick()
        }
    }

    const renderMessage = () => {
        if (React.isValidElement(trayParams.message)) {
            return trayParams.message
        }
        return <div className={styles.confirmMessageText}>{trayParams.message}</div>
    }

    useEffect(() => {
        const handleKeyDown = event => {
            if (event.key === 'Enter') {
                // Enter pressed for something else (sending a reply) must not confirm it.
                if (trayParams?.disableEnterConfirm) return
                if (reasonInput && event.target instanceof HTMLTextAreaElement) {
                    return
                }
                onConfirm()
            } else if (event.key === 'Escape') {
                onCancel()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
        }
    }, [trayParams, reasonInput, reasonValue])

    useEffect(() => {
        setReasonValue(reasonInput?.defaultValue || '')
    }, [reasonInput?.defaultValue, trayParams])

    useEffect(() => {
        openedAtRef.current = Date.now()
    }, [trayParams])

    useEffect(() => {
        return () => {
            if (!didActRef.current && trayParams?.onCancel) {
                trayParams.onCancel()
            }
        }
    }, [])

    const actionButtonClass =
        confirmVariant === 'usd' ? styles.stripeButtonPrimary : styles.bsvButton

    return (
        <Tray
            title={trayParams?.title || 'Confirm'}
            onDismiss={onCancel}
            preventEscClose={isConfirming}
            trayClassName={styles.confirmTray}
            bodyClassName={styles.confirmTrayBody}
        >
            {/* Long content (a wallet prompt's summary and details) scrolls here, so the buttons
                below stay on screen. */}
            <div className={styles.confirmScroll}>
                <div className={styles.confirmMessageContainer}>{renderMessage()}</div>

                {reasonInput && (
                    <label className={styles.reasonField}>
                        <span className={styles.reasonLabel}>{reasonInput.label || 'Reason'}</span>
                        <textarea
                            className={styles.reasonInput}
                            value={reasonValue}
                            placeholder={reasonInput.placeholder || 'No reason given.'}
                            rows={4}
                            onChange={event => setReasonValue(event.target.value)}
                        />
                    </label>
                )}
            </div>

            <div className={styles.confirmFooter}>
                <div className={styles.confirmActions}>
                    {!trayParams.hideCancel && (
                        <button className={styles.cancelButton} onClick={onCancel}>
                            {trayParams.cancelMessage || 'Cancel'}
                        </button>
                    )}

                    {primaryActionFirst && !hideConfirm && (
                        <button
                            className={actionButtonClass}
                            onClick={onConfirm}
                            disabled={!!trayParams?.confirmDisabled || isConfirming}
                            aria-busy={isConfirming}
                            style={
                                trayParams?.confirmDisabled || isConfirming
                                    ? { opacity: 0.5, cursor: 'not-allowed' }
                                    : {}
                            }
                        >
                            {isConfirming && (
                                <span className={styles.confirmButtonSpinner} aria-hidden="true" />
                            )}
                            {isConfirming
                                ? trayParams.confirmPendingLabel || 'Working…'
                                : trayParams.confirmLabel || 'Got it!'}
                        </button>
                    )}

                    {Array.isArray(trayParams.extraActions) &&
                        trayParams.extraActions.map((action, idx) => {
                            const variant = action?.variant || 'secondary'
                            const className =
                                variant === 'usd' || variant === 'primary'
                                    ? styles.stripeButtonPrimary
                                    : variant === 'bsv'
                                    ? styles.bsvButton
                                    : styles.stripeButton

                            return (
                                <button
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`${action?.label || 'extra'}-${idx}`}
                                    className={className}
                                    disabled={!!action?.disabled}
                                    style={
                                        action?.disabled
                                            ? { opacity: 0.5, cursor: 'not-allowed' }
                                            : {}
                                    }
                                    onClick={() => onExtraAction(action)}
                                >
                                    {action?.label}
                                </button>
                            )
                        })}

                    {!primaryActionFirst && !hideConfirm && (
                        <button
                            className={actionButtonClass}
                            onClick={onConfirm}
                            disabled={!!trayParams?.confirmDisabled || isConfirming}
                            aria-busy={isConfirming}
                            style={
                                trayParams?.confirmDisabled || isConfirming
                                    ? { opacity: 0.5, cursor: 'not-allowed' }
                                    : {}
                            }
                        >
                            {isConfirming && (
                                <span className={styles.confirmButtonSpinner} aria-hidden="true" />
                            )}
                            {isConfirming
                                ? trayParams.confirmPendingLabel || 'Working…'
                                : trayParams.confirmLabel || 'Got it!'}
                        </button>
                    )}
                </div>

                {Array.isArray(trayParams?.setupLinks) && trayParams.setupLinks.length > 0 && (
                    <div className={styles.setupLinks}>
                        {trayParams.setupLinks.map((link, idx) => (
                            <button
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${link?.label || 'setup'}-${idx}`}
                                className={
                                    link?.variant === 'usd'
                                        ? styles.setupLinkUsd
                                        : link?.variant === 'bsv'
                                        ? styles.setupLinkBsv
                                        : styles.setupLink
                                }
                                disabled={!!link?.disabled}
                                onClick={() => onSetupLink(link)}
                            >
                                {link?.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </Tray>
    )
}
